import { IconArrowForward } from "@tine/designsystem-icons/sharp";
import {
   LogoBiola,
   LogoDovreYsteri,
   LogoGryr,
   LogoIste,
   LogoJarlsberg,
   LogoLitago,
   LogoNoisy,
   LogoNorvegia,
   LogoSelbuBlaa,
   LogoSnofrisk,
   LogoSunniva,
   LogoYt
} from "@tine/web-logos/brands";
import type { EntryFieldTypes } from "contentful";
import { useInView } from "react-intersection-observer";
import { Link } from "wouter";

import { sendSelectTeaser, sendViewTeaser } from "../common/tracking";
import type { ContentfulLogos } from "../common/types/cmsTypes";
import type { TypeLogosFields } from "../common/types/contentful";

import FullWidthBackground from "./FullWidthBackground";

type LogoName = TypeLogosFields["logos"] extends EntryFieldTypes.Array<EntryFieldTypes.Symbol<infer T>> ? T : never;

type LogoListProps = {
   content: ContentfulLogos;
};

const LogoList = ({ content }: LogoListProps) => {
   const title = content.fields.title;
   const logos = content.fields.logos;
   const linkText = content.fields.linkText;
   const linkUrl = content.fields.linkUrl;
   const backgroundColor = content.fields.background;
   const logoClasses = "w-16 lg:w-24";
   const contentType = content.sys.contentType.sys.id;

   const logoComponents: Record<LogoName, JSX.Element> = {
      Norvegia: <LogoNorvegia className={logoClasses} />,
      Jarlsberg: <LogoJarlsberg className={logoClasses} />,
      Sunniva: <LogoSunniva className={logoClasses} />,
      Iste: <LogoIste className={logoClasses} />,
      Yt: <LogoYt className={logoClasses} />,
      Selbu: <LogoSelbuBlaa className={logoClasses} />,
      Biola: <LogoBiola className={logoClasses} />,
      Snofrisk: <LogoSnofrisk className={logoClasses} />,
      Dovre: <LogoDovreYsteri className={logoClasses} />,
      Litago: <LogoLitago className={logoClasses} />,
      Noisy: <LogoNoisy className={logoClasses} />,
      Gryr: <LogoGryr className={logoClasses} />
   };

   const trackImpression = (isVisible: boolean) => {
      if (isVisible) {
         sendViewTeaser(title, contentType);
      }
   };

   const { ref: viewRef } = useInView({
      triggerOnce: true,
      onChange: trackImpression
   });

   const handleClickLogoLink = () => {
      sendSelectTeaser(title, contentType, undefined, `/${linkUrl}`);
   };

   return (
      <div ref={viewRef} className="relative flex flex-col w-full">
         {backgroundColor && <FullWidthBackground color="bg-background-tinted" />}
         {title && <h2 className="px-10 pt-8 text-xl font-semibold text-center lg:pt-12 lg:text-2xl">{title}</h2>}
         <div className="flex max-w-[800px] flex-wrap items-center justify-center gap-10 self-center px-10 py-10 lg:px-0 lg:py-12">
            {Object.keys(logoComponents).map((logoName) =>
               logos.includes(logoName as LogoName) ? <div key={logoName}>{logoComponents[logoName as LogoName]}</div> : null
            )}
         </div>

         {linkUrl && linkText && (
            <Link to={linkUrl} onClick={handleClickLogoLink} className="flex flex-row items-center justify-center pb-8 lg:pb-12">
               <span className="text-base">{linkText}</span>
               <IconArrowForward />
            </Link>
         )}
      </div>
   );
};

export default LogoList;
